import { useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import { get, isEmpty } from 'lodash';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { Fragment, useMemo } from 'react';
import { toast } from 'react-toastify';
import Breadcrumb from '~/components/Breadcrumb';
import Button from '~/components/Button';
import Container from '~/components/Container';
import Input from '~/components/Input';
import SEOTags from '~/components/SEOTags';
import Text from '~/components/Text';
import TextArea from '~/components/TextArea';
import { QUERY_GET_CMS_PAGE } from '~/data/cms/gql';
import { GET_CUSTOM_FORM_GQL, QUERY_GET_CUSTOM_FORM } from '~/data/home';
import { CustomForm, CustomFormDField } from '~/data/home/interface';
import { GET_SCHEMA_CONTACT } from '~/data/schemas/schema.gql';
import Layout from '~/layouts/ThemeLayout';
import ContactSchema from '~/schema/ContactSchema';
import { initializeApollo } from '~/utils/apollo-client';
import { isValidEmail } from '~/utils/validators';

const getText = (domNode: any) => {
    let text = '';
    domNode?.children?.forEach((child: any) => {
        if (child.type === 'text') {
            text += child.data;
        } else {
            text += getText(child);
        }
    });
    return text;
};

const CmsPage = ({ data, loading, formdata, configSchema }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const cmsPage = get(data, 'cmsPage.content', '') as string;
    // const { error, data:formdata, refetch, networkStatus } = useQuery(QUERY_GET_CUSTOM_FORM, {
    //     variables: {
    //         id: process.env.NEXT_PUBLIC_CUSTOM_FORM_CONTACT
    //     },
    //     notifyOnNetworkStatusChange: true
    // });

    const [submitForm, { loading: isSignUpLoading }]: any = useMutation(GET_CUSTOM_FORM_GQL, {
        onCompleted: () => {
            toast.success('Submit Form successful');
            formik.resetForm();
        },
        onError: ({ message }) => {
            toast.error(message);
        }
    });

    const { meta_description, meta_title, meta_keywords, url_key, title } = data?.cmsPage || {};
    const customForm: CustomForm = useMemo(() => {
        return formdata?.data?.customForm || [];
    }, [formdata]);

    const initialValuesFields: any = {};
    customForm.fields?.forEach((i) => {
        initialValuesFields[i.code] = '';
    });

    const formik = useFormik({
        initialValues: initialValuesFields,
        validateOnChange: false,
        validateOnBlur: true,
        validate(values: any) {
            const errors: Record<string, string> = {};
            customForm.fields?.map((field: CustomFormDField) => {
                if (!!field.is_required && !values[field.code]?.trim()) {
                    errors[field.code] = `${field.title} is required`;
                } else {
                    if (field.validator === 'validate-email' && !isValidEmail(values[field.code])) {
                        errors[field.code] = `Please enter a valid email address`;
                    }
                }
            });
            return errors;
        },
        onSubmit: async (values) => {
            const fields: any = [];
            const errors: Record<string, string> = {};
            customForm.fields?.map((field: CustomFormDField) => {
                if (field.field_type !== 'multiselect') {
                    fields.push({
                        key: field.code,
                        value: values?.[field.code]
                    });
                } else {
                    if (field.validator === 'validate-email' && !isValidEmail(values[field.code])) {
                        errors[field.code] = `Please enter a valid email address`;
                    }
                }
            });
            return await submitForm({
                variables: {
                    form_id: Number(process.env.NEXT_PUBLIC_CUSTOM_FORM_CONTACT),
                    fields: fields,
                    fields_multi: []
                }
            });
        }
    });

    const renderFiled = (field: CustomFormDField, formik: any) => {
        switch (field?.field_type) {
            case 'textarea':
                return (
                    <div className="col-span-2">
                        <TextArea
                            {...formik.getFieldProps(`${field.code}`)}
                            error={
                                (!!field?.code &&
                                    formik.touched?.[field?.code] &&
                                    !!formik?.errors?.[field.code] &&
                                    (formik?.errors?.[field.code] as string)) ||
                                ''
                            }
                            label={field?.title}
                            rows={4}
                            required={field?.is_required}
                            className="w-ful"
                        />
                    </div>
                );
            default:
                return (
                    <div>
                        <Input
                            type={field?.field_type}
                            label={field?.title}
                            required={field?.is_required}
                            {...formik.getFieldProps(`${field.code}`)}
                            error={
                                (!!field?.code &&
                                    formik.touched?.[field?.code] &&
                                    !!formik?.errors?.[field.code] &&
                                    (formik?.errors?.[field.code] as string)) ||
                                ''
                            }
                        />
                    </div>
                );
        }
    };

    return (
        <Layout>
            <SEOTags
                title={meta_title || 'contact-us'}
                description={meta_description}
                keywords={meta_keywords}
                url={url_key}
            />
            <ContactSchema config={configSchema} />
            <Container rootClassName="py-5 lg:py-8" className="flex flex-col gap-4 cms-page">
                <Breadcrumb items={[{ title: 'Home', path: '/' }, { title: title }]} />

                <div className="flex flex-col lg:flex-row gap-4">
                    {customForm?.fields?.length && (
                        <div className="w-full border px-8 py-4 rounded-sm">
                            <Text type="title-2" className="mb-4">
                                {title}
                            </Text>
                            {/*  form */}
                            <div className="flex gap-2">
                                <div className="grid grid-cols-2 gap-4 flex-1">
                                    {!!customForm?.fields?.length
                                        ? customForm?.fields?.map((item, index) => {
                                              return <Fragment key={index}>{renderFiled(item, formik)}</Fragment>;
                                          })
                                        : ''}
                                </div>
                            </div>
                            <div className="mt-2 text-right w-full">
                                <Button
                                    loading={isSignUpLoading}
                                    onClick={() => formik.handleSubmit()}
                                    className="ml-auto"
                                >
                                    Submit Contact
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className="w-full overflow-auto" dangerouslySetInnerHTML={{ __html: cmsPage }}></div>
                </div>
            </Container>
        </Layout>
    );
};

export default CmsPage;

export async function getServerSideProps({ params }: GetServerSidePropsContext) {
    const apolloClient = initializeApollo({
        httpLinkProps: { useGETForQueries: true }
    });

    let res = {} as CmsPageProps & {
        formdata: any;
        configSchema: any;
    };

    try {
        const formdata = await apolloClient.query({
            query: QUERY_GET_CUSTOM_FORM,
            variables: {
                id: 2
            },
            context: { fetchOptions: { method: 'GET' } }
        });

        const data = await apolloClient.query({
            query: QUERY_GET_CMS_PAGE,
            variables: {
                identifier: 'contact-us'
            },
            context: { fetchOptions: { method: 'GET' } }
        });
        const { data: dataSchema } = await apolloClient.query({
            query: GET_SCHEMA_CONTACT
        });
        res = {
            ...data,
            formdata,
            configSchema: dataSchema?.MarketingConfig?.schemaContact
        };
    } catch (error) {
        console.error(error, 'QUERY_SEO_CATEGORY');
    }

    if (isEmpty(res)) {
        return {
            notFound: true
        };
    }

    return { props: { ...res } };
}
